import React from 'react';
import '../styles/SellSurplus.css';
import offerImage from '../assets/electrical-offer-image.png';
import seamlessImage from '../assets/seamless-image.png';
import valuationImage from '../assets/valuation-image.png';
import professionalImage from '../assets/professional-image.png';
import eqImg from '../assets/electrical-types.png';
import eq2Img from '../assets/electrical-types2.png'
import clientsImg from '../assets/our-clients.png';
import OfferForm from './OfferForm';
import Footer from './Footer';
import { useEffect, useRef } from 'react';

export default function SellElectrical() {
  const scrollToGetStarted = () => {
    document.getElementById('get-started').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="sell-surplus-container">

      {/* Sell Electrical — metadata (React 19 hoists into <head>) */}
      <title>We Buy Surplus Electrical Equipment — Breakers, Switchgear, MCCs</title>
      <meta
        name="description"
        content="Sell surplus electrical equipment—breakers, switchgear, MCCs, drives and more. Fast quotes, insured nationwide pickup, payment in 24–48h. Get a guaranteed offer."
      />
      <link rel="canonical" href="https://sdindustrialsurplus.com/sell-electrical" />
      <meta name="robots" content="index,follow" />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://sdindustrialsurplus.com/sell-electrical" />
      <meta property="og:site_name" content="S&D Industrial Surplus" />
      <meta property="og:title" content="We Buy Surplus Electrical Equipment — Fast Quotes & Pickup" />
      <meta property="og:description" content="Transparent pricing, insured logistics, and 24–48h payment. Upload your list or call to get your guaranteed offer." />
      <meta property="og:image" content="https://sdindustrialsurplus.com/logo192.png" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="We Buy Surplus Electrical Equipment — Fast Quotes & Pickup" />
      <meta name="twitter:description" content="Transparent pricing, insured logistics, and 24–48h payment. Upload your list or call to get your guaranteed offer." />
      <meta name="twitter:image" content="https://sdindustrialsurplus.com/logo192.png" />

      {/* Optional: Service schema for this page */}
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Surplus Electrical Equipment Purchasing",
        "serviceType": "We buy breakers, switchgear, MCCs, drives and related electrical equipment",
        "areaServed": "US",
        "provider": { "@type": "Organization", "name": "S&D Industrial Surplus", "url": "https://sdindustrialsurplus.com/" },
        "url": "https://sdindustrialsurplus.com/sell-electrical"
      })}</script>



      <GetOfferCTA onOfferClick={scrollToGetStarted} />
      <HowItWorks />
      <div className="sbs-wrapper">
        <EquipmentTypes />
        <TechnicalSpecifications />
        <WhoWeBuyFrom />
      </div>
      <OfferForm />
      <Footer />
    </div>
  );
}

function GetOfferCTA({ onOfferClick }) {
  return (
    <section className="get-offer-cta">
      <div className="offer-left">
        <h1>
          We Buy Surplus Electrical Equipment, Nationwide Pickup
        </h1>
        <p>
          We buy breakers, switchgear, MCCs, and more nationwide. Click
          <em> Get an Offer</em>, fill out the quick form, and receive a
          no‑obligation quote fast—or call toll‑free <a href="tel:8328852369" className="phone-link"> (832) 885-2369</a>.
        </p>
        <span className="btn-offer-wrap">
          <button className="btn-offer" onClick={onOfferClick}>
            Get an offer
          </button>
        </span>
      </div>
      <div className="offer-right">
        <img src={offerImage} data-smart-multimime="1" alt="Illustration of electrical equipment quote" />
      </div>
    </section>
  );
}

function HowItWorks() {
  const railRef = useRef(null);

  // Pure drag-to-scroll (NO momentum), doesn't trap page scroll
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;

    let isDown = false;
    let startX = 0;
    let startLeft = 0;
    let moved = false;
    let activePointerId = null;

    const clampScroll = (val) => {
      const max = Math.max(0, el.scrollWidth - el.clientWidth);
      return Math.min(Math.max(0, val), max);
    };

    const getX = (e) => e.clientX ?? e.touches?.[0]?.clientX ?? 0;

    const onDown = (e) => {
      isDown = true;
      moved = false;
      activePointerId = e.pointerId ?? null;
      startX = getX(e);
      startLeft = el.scrollLeft;

      // Smooth drag UX without blocking page scroll:
      // avoid default text selection by temporarily disabling it
      el.style.userSelect = 'none';
      el.classList.add('is-dragging'); // keep cursor as "grab", not "grabbing"
      el.setPointerCapture?.(activePointerId);

      // Prevent selection start; don't block wheel/scroll on the page
      if (e.cancelable) e.preventDefault();
    };

    const onMove = (e) => {
      if (!isDown) return;
      const x = getX(e);
      const dx = x - startX;
      el.scrollLeft = clampScroll(startLeft - dx);
      if (Math.abs(dx) > 2) moved = true;
      // No preventDefault here—lets the page still process natural scroll elsewhere
    };

    const releaseCapture = () => {
      if (activePointerId != null && el.hasPointerCapture?.(activePointerId)) {
        el.releasePointerCapture(activePointerId);
      }
      activePointerId = null;
    };

    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      el.style.userSelect = '';
      el.classList.remove('is-dragging');
      releaseCapture();
      // No momentum/inertia—stop instantly
    };

    const onLeave = () => { if (isDown) onUp(); };

    // Swallow clicks only if we actually dragged
    const onClickCapture = (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    el.addEventListener('pointerdown', onDown, { passive: false });
    el.addEventListener('pointermove', onMove, { passive: true });
    el.addEventListener('pointerup', onUp, { passive: true });
    el.addEventListener('pointercancel', onUp, { passive: true });
    el.addEventListener('pointerleave', onLeave, { passive: true });
    el.addEventListener('click', onClickCapture, true);

    return () => {
      el.style.userSelect = '';
      el.classList.remove('is-dragging');
      releaseCapture();
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('pointerleave', onLeave);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, []);

  // Only show grab cursor when actual horizontal overflow exists
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;

    const update = () => {
      const hasOverflow = (el.scrollWidth - el.clientWidth) > 1;
      el.classList.toggle('has-x-overflow', hasOverflow);
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true });

    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  const steps = [
    {
      img: seamlessImage,
      title: '1. Seamless Communication',
      desc: `We keep you in the loop at every step of your industrial transformer, PVF & electrical surplus sale—whether it’s scheduling a pick-up, sending photos, or answering questions, you get instant, convenient updates and support.`,
    },
    {
      img: valuationImage,
      title: '2. Efficient Valuations & Prompt Payments',
      desc: `Our team leverages market data to deliver fast, accurate valuations on your electrical industrial surplus—transformers, switchgear, PVF and more—and issues payments promptly, so you get the best offer without the wait.`,
    },
    {
      img: professionalImage,
      title: '3. Professional Handling of Your Assets',
      desc: `From careful on-site inspection to insured transport, we manage your surplus transformers, PVF piping, and other electrical equipment with top-tier safety protocols and professional handling—no liability, no hassle.`,
    },
  ];

  return (
    <section className="how-it-works-section">
      <h2 className="how-it-works-heading">
        How Our Surplus Equipment Buying Process Works
      </h2>
      {/* Note: no data-lenis-prevent here */}
      <div className="how-it-works-grid" ref={railRef}>
        {steps.map(({ img, title, desc }, i) => (
          <div className="how-it-works-card" key={i}>
            <img src={img} alt="" className="how-it-works-img" />
            <h3 className="how-it-works-title">{title}</h3>
            <p className="how-it-works-desc">{desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function EquipmentTypes() {
  const ITEMS = [
    {
      label: 'Low‑Voltage Circuit Breakers',
      note: 'Molded‑case & insulated‑case • 15‑4,000 A',
    },
    {
      label: 'Medium‑Voltage Breakers',
      note: 'Vacuum & SF₆ • 5‑15 kV',
    },
    {
      label: 'Switchgear & Switchboards',
      note: 'LV & MV lineups, sections, buckets',
    },
    {
      label: 'Motor Control Centers (MCCs)',
      note: 'Complete centers or individual buckets',
    },
    {
      label: 'Drives & Soft Starters',
      note: 'VFDs 1‑5,000 HP, reduced‑voltage starters',
    },
    {
      label: 'Panelboards & Disconnects',
      note: 'NEMA 1, 3R, 4X enclosures',
    },
  ];

  return (
    <section className="side-by-side-section">
      <div className="sbs-image">
        <img src={eqImg} alt="Assorted electrical equipment" />
      </div>

      <div className="sbs-content">
        <h2>Types&nbsp;of&nbsp;Electrical&nbsp;Equipment&nbsp;We&nbsp;Buy</h2>

        <div className="equip-grid">
          {ITEMS.map(({ label, note }) => (
            <div className="equip-item" key={label}>
              <span className="equip-icon" aria-hidden />
              <div className="equip-text">
                <strong>{label}</strong>
                <small>{note}</small>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TechnicalSpecifications() {
  const SPECS = [
    {
      label: 'Voltage Range',
      note: '208 V – 15 kV',
    },
    {
      label: 'Interrupt Rating',
      note: '25 – 200 kAIC',
    },
    {
      label: 'Enclosure Type',
      note: 'NEMA 1, 3R, 4, 4X, 12',
    },
    {
      label: 'Frame Sizes',
      note: '15 A – 6,000 A',
    },
    {
      label: 'Motor HP Range',
      note: '1 – 5,000 HP (VFDs & starters)',
    },
  ];

  return (
    <section className="side-by-side-section">
      <div className="sbs-content">
        <h2>Technical&nbsp;Specifications&nbsp;We&nbsp;Consider</h2>
        <div className="equip-grid">
          {SPECS.map(({ label, note }) => (
            <div className="equip-item" key={label}>
              <span className="equip-icon" aria-hidden />
              <div className="equip-text">
                <strong>{label}</strong>
                <small>{note}</small>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="sbs-image">
        <img src={eq2Img} alt="Electrical equipment technical details" />
      </div>
    </section>
  );
}

function WhoWeBuyFrom() {
  return (
    <section className="side-by-side-section">
      <div className="sbs-content">
        <h2>Who We Buy From</h2>
        <p>
          We purchase surplus electrical gear from utilities, industrial plants,
          data centers, OEMs, and electrical contractors across North America.
          Whether you’re upgrading switchgear, decommissioning a plant, or
          clearing warehouse stock, we provide top‑of‑market offers and turnkey
          logistics.
        </p>
      </div>
      <div className="sbs-image">
        <img src={clientsImg} alt="Satisfied electrical equipment sellers" />
      </div>
    </section>
  );
}
